import type { RealtimeChannel, SupabaseClient } from '@supabase/supabase-js'
import { getSupabaseAuthClient } from './index'
import { updateNotificationPreferences } from '../account'

// The shared session client is created without a schema generic, so its
// query-builder types `.update()/.delete()` payloads as `never`. Notifications
// are a simple, well-known table; treat the client as loosely typed for these
// mutations rather than threading a full generated Database type through.
const asLooseClient = (client: ReturnType<typeof getSupabaseAuthClient>) =>
  client as unknown as SupabaseClient

/**
 * In-app notifications, backed by Supabase `user_notifications`.
 *
 * Security: the table is RLS-scoped per user. The policy matches
 * `user_id = public.current_account_id()`, and `current_account_id()` resolves
 * the authenticated Supabase user (`auth.uid()`) to their `accounts.id` via
 * `private.account_identity`. We therefore use the app's session-authenticated
 * Supabase client (getSupabaseAuthClient), whose JWT carries the logged-in user;
 * PostgREST and Realtime both honour the same RLS. No Firebase token is involved.
 */

export const PAGE_SIZE = 10

export interface UserNotification {
  id: string
  user_id: string
  type: string
  title: string
  body: string
  metadata: Record<string, string> | null
  read: boolean
  created_at: string
}

/** First page of the user's notifications, newest first. */
export const fetchNotifications = async (before?: string): Promise<UserNotification[]> => {
  const client = getSupabaseAuthClient()
  if (!client) return []
  let q = client
    .from('user_notifications')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(PAGE_SIZE)
  if (before) q = q.lt('created_at', before)
  const { data, error } = await q
  if (error) {
    console.error('fetchNotifications failed', error)
    return []
  }
  return (data ?? []) as UserNotification[]
}

// Each subscriber needs its OWN channel topic. supabase-js dedupes channels by
// topic on the (singleton) client, so reusing a fixed name across subscribers —
// e.g. the header bell AND the tray — returns the already-subscribed channel and
// makes the second .on(...) throw "cannot add postgres_changes callbacks ...
// after subscribe()". A monotonic suffix keeps every subscription independent.
let notificationChannelSeq = 0

/**
 * Realtime subscription to the user's notifications. Calls onChange whenever a
 * row is inserted/updated/deleted for this user (RLS limits delivery to own rows).
 * Returns an unsubscribe function. The caller typically re-fetches the first page
 * on change to refresh the tray + unread badge.
 */
export const subscribeToNotifications = (onChange: () => void): (() => void) => {
  const client = getSupabaseAuthClient()
  if (!client) return () => undefined

  notificationChannelSeq += 1
  const channel: RealtimeChannel = client
    .channel(`user-notifications-${notificationChannelSeq}`)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'user_notifications' },
      () => onChange()
    )
    .subscribe((status, err) => {
      if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
        console.error('user_notifications subscription failed', status, err)
      }
    })

  return () => {
    // removeChannel (not just unsubscribe) frees the topic so a later
    // subscription with a fresh name starts clean.
    void client.removeChannel(channel)
  }
}

/**
 * Realtime subscription to the account's notification preferences row. Fires when
 * preferences change anywhere — including outside the settings UI, e.g. the email
 * unsubscribe flow or another tab/device — so callers can refresh what they show.
 * RLS limits delivery to the signed-in user's own row, so no id filter is needed
 * (the frontend's account id can be a legacy uid rather than accounts.id).
 * Returns an unsubscribe fn.
 */
export const subscribeToNotificationPreferences = (onChange: () => void): (() => void) => {
  const client = getSupabaseAuthClient()
  if (!client) return () => undefined

  notificationChannelSeq += 1
  const channel: RealtimeChannel = client
    .channel(`notification-preferences-${notificationChannelSeq}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'account_notification_preferences'
      },
      () => onChange()
    )
    .subscribe((status, err) => {
      if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
        console.error('account_notification_preferences subscription failed', status, err)
      }
    })

  return () => {
    void client.removeChannel(channel)
  }
}

export const markNotificationRead = async (notificationId: string): Promise<void> => {
  const client = getSupabaseAuthClient()
  if (!client) return
  const { error } = await asLooseClient(client)
    .from('user_notifications')
    .update({ read: true })
    .eq('id', notificationId)
  if (error) console.error('markNotificationRead failed', error)
}

export const markAllNotificationsRead = async (notificationIds: string[]): Promise<void> => {
  const client = getSupabaseAuthClient()
  if (!client || notificationIds.length === 0) return
  const { error } = await asLooseClient(client)
    .from('user_notifications')
    .update({ read: true })
    .in('id', notificationIds)
  if (error) console.error('markAllNotificationsRead failed', error)
}

export const deleteNotification = async (notificationId: string): Promise<void> => {
  const client = getSupabaseAuthClient()
  if (!client) return
  const { error } = await asLooseClient(client)
    .from('user_notifications')
    .delete()
    .eq('id', notificationId)
  if (error) console.error('deleteNotification failed', error)
}

export const deleteNotifications = async (notificationIds: string[]): Promise<void> => {
  const client = getSupabaseAuthClient()
  if (!client || notificationIds.length === 0) return
  const { error } = await asLooseClient(client)
    .from('user_notifications')
    .delete()
    .in('id', notificationIds)
  if (error) console.error('deleteNotifications failed', error)
}

// ─── Notification settings ───────────────────────────────────────────────────
// Notification preferences live on the Supabase account (via account-core's
// updateNotificationPreferences), NOT in Firestore. The UI still passes the
// legacy snake_case blobs ({ notifications } / { notification_privacy }); this
// maps them to account-core's camelCase Partial<AccountNotificationPreferences>.

const NOTIFICATION_SETTING_KEY_MAP: Record<string, string> = {
  in_app_notifications_enabled: 'inAppNotificationsEnabled',
  push_notifications_enabled: 'pushNotificationsEnabled',
  email_notifications_enabled: 'emailNotificationsEnabled',
  notify_user_followed_you: 'notifyUserFollowedYou',
  notify_friend_took_action: 'notifyFriendTookAction',
  notify_new_action_matching_interests: 'notifyNewActionMatchingInterests',
  notify_new_action_in_your_area: 'notifyNewActionInYourArea',
  notify_action_potential_credited: 'notifyActionPotentialCredited',
  allow_action_taken_notifications: 'allowActionTakenNotifications',
  allow_action_favorited_notifications: 'allowActionFavoritedNotifications'
}

const toCamelPreferences = (settings: Record<string, unknown>): Record<string, boolean> => {
  const nested = {
    ...((settings.notifications as Record<string, unknown> | undefined) ?? {}),
    ...((settings.notification_privacy as Record<string, unknown> | undefined) ?? {})
  }
  // Support both the nested blobs and a flat snake_case object.
  const source = Object.keys(nested).length > 0 ? nested : settings
  const mapped: Record<string, boolean> = {}
  Object.entries(source).forEach(([key, value]) => {
    const camelKey = NOTIFICATION_SETTING_KEY_MAP[key]
    if (camelKey && typeof value === 'boolean') {
      mapped[camelKey] = value
    }
  })
  return mapped
}

/**
 * Persist notification settings to the Supabase account. Accepts the legacy
 * snake_case shape used across the notification UI (nested `notifications` /
 * `notification_privacy` blobs, or a flat snake_case object) and forwards the
 * camelCase subset to account-core.
 */
export const updateNotificationSettings = async (
  accountId: string,
  settings: Record<string, unknown>
): Promise<void> => {
  const preferences = toCamelPreferences(settings)
  if (Object.keys(preferences).length === 0) return
  await updateNotificationPreferences(accountId, preferences)
}

// ─── Debug ───────────────────────────────────────────────────────────────────

const DEBUG_NOTIFICATIONS_PATH = '/.netlify/functions/debug-notifications'

const getSessionToken = async () => {
  const client = getSupabaseAuthClient()
  return client ? (await client.auth.getSession()).data.session?.access_token : undefined
}

/**
 * Public on/off switch for the notification debug tool in this build. When it isn't
 * "true", the panel never renders and no access check is made. It's only a UI
 * short-circuit — the debug-notifications function still enforces its own allowlist,
 * and the Supabase function its own production guard.
 */
export const isNotificationDebugEnabled = () =>
  process.env.GATSBY_NOTIFICATION_DEBUG_ENABLED === 'true'

/**
 * Whether the signed-in user may use the notification debug panel. The allowlist
 * is private (server-side only), so the panel asks rather than reading it.
 */
export const checkDebugNotificationsAccess = async (): Promise<boolean> => {
  // Skip the server round trip entirely when debugging is switched off
  if (!isNotificationDebugEnabled()) return false
  try {
    const token = await getSessionToken()
    if (!token) return false
    const response = await fetch(DEBUG_NOTIFICATIONS_PATH, {
      headers: { Authorization: `Bearer ${token}` }
    })
    if (!response.ok) return false
    const data = (await response.json()) as { allowed?: boolean }
    return data.allowed === true
  } catch {
    return false
  }
}

/**
 * Sends a test notification of `type` to the signed-in user (allowlisted emails
 * only). The Netlify function derives the recipient from the session token, so
 * no user id is sent.
 */
export const sendDebugNotification = async (type: string): Promise<void> => {
  const token = await getSessionToken()
  if (!token) throw new Error('You must be signed in to send debug notifications')

  const response = await fetch(DEBUG_NOTIFICATIONS_PATH, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ type })
  })
  if (!response.ok) {
    const data = await response.json().catch(() => ({}))
    throw new Error((data as { error?: string }).error || `Request failed (${response.status})`)
  }
}
